const BOARD_DATA_BASE_URL = 'https://abet-board-data.abetco.workers.dev/data'
const PROOF_DATA_BASE_URL = 'https://abet-scraper-proof-portal.vercel.app/data'
const MASTER_LIST_URL = 'https://sportsbook-master-list.vercel.app/books_comprehensive.json'

const SOURCE_KEYS = [
  ['cloudflare_worker', 'CF'],
  ['huggingface_space', 'HF'],
  ['oracle_vm', 'OR'],
  ['github_actions', 'GH'],
]

async function fetchArtifact(baseUrl, name, timeoutMs = 15000) {
  const response = await fetch(`${baseUrl}/${name}`, {
    cache: 'no-store',
    signal: AbortSignal.timeout(timeoutMs),
  })
  if (!response.ok) throw new Error(`${name} unavailable (HTTP ${response.status})`)
  return response.json()
}

async function fetchJsonUrl(url, timeoutMs = 15000) {
  const response = await fetch(url, {
    cache: 'no-store',
    signal: AbortSignal.timeout(timeoutMs),
  })
  if (!response.ok) throw new Error(`${url} unavailable (HTTP ${response.status})`)
  return response.json()
}

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function parseTime(value) {
  const parsed = value ? new Date(value).getTime() : Number.NaN
  return Number.isFinite(parsed) ? parsed : null
}

function latestSourceCheck(sources) {
  const checks = Object.values(sources || {})
    .map((source) => parseTime(source?.checked_utc))
    .filter(Number.isFinite)
  return checks.length > 0 ? new Date(Math.max(...checks)).toISOString() : null
}

function indexByBook(rows) {
  return new Map(asArray(rows).map((row) => [String(row?.book || '').toLowerCase(), row]))
}

function countMarkets(inventoryRow) {
  return asArray(inventoryRow?.markets).length || Number(inventoryRow?.n_markets || 0)
}

function normalizeContinuousState(book, releaseState) {
  const continuous = book?.continuous || book?.cadence || null
  if (continuous?.verified === true && continuous?.enabled === true) return 'running'
  if (continuous?.verified === true && continuous?.enabled === false) return 'stopped'
  if (continuous?.status) return String(continuous.status).toLowerCase()
  if (releaseState === 'current') return 'release-current-unverified'
  return 'unverified'
}

export function buildScraperCoverageRows(bundle, nowMs = Date.now()) {
  const fleetBooks = bundle?.fleet?.books || {}
  const matrixByBook = indexByBook(bundle?.matrix?.books)
  const inventoryByBook = indexByBook(bundle?.inventory?.books)
  const marriageByBook = indexByBook(bundle?.marriage?.books)
  const joinedBooks = new Set(asArray(bundle?.manifest?.books).map((book) => String(book).toLowerCase()))
  const completenessBooks = bundle?.completeness?.books || {}
  const releaseCapturedAt = bundle?.manifest?.captured_utc || bundle?.release?.captured_utc || null
  const releaseAgeMs = parseTime(releaseCapturedAt) == null ? null : Math.max(0, nowMs - parseTime(releaseCapturedAt))
  const releaseState = releaseAgeMs == null ? 'unavailable' : releaseAgeMs <= 120000 ? 'current' : 'stale'

  return Object.entries(fleetBooks).map(([bookKey, book]) => {
    const key = bookKey.toLowerCase()
    const matrix = matrixByBook.get(key)
    const inventory = inventoryByBook.get(key)
    const marriage = marriageByBook.get(key)
    const sourceCells = SOURCE_KEYS.map(([sourceKey, label]) => {
      const source = book?.sources?.[sourceKey] || null
      return {
        key: sourceKey,
        label,
        ok: source?.ok === true,
        status: source?.status || 'UNVERIFIED',
        latencySeconds: Number.isFinite(Number(source?.latency_s)) ? Number(source.latency_s) : null,
        rawBytes: Number(source?.raw_bytes || 0),
        checkedAt: source?.checked_utc || null,
        url: source?.url || null,
      }
    })
    const sourceCount = sourceCells.filter((source) => source.ok).length
    const joined = joinedBooks.has(key)
    const exhaustiveVerified = book?.exhaustive?.verified === true || inventory?.exhaustive_verified === true
    const mapped = Boolean(matrix?.game_lines_mapped || inventory?.tier === 'mapped-exact')
    const proofPath = book?.dedicated_page || `/proof?book=${encodeURIComponent(key)}`

    return {
      key,
      display: book?.display || matrix?.display || inventory?.name || key,
      active: book?.active !== false,
      sourceCells,
      sourceCount,
      anyLive: sourceCount > 0,
      onDemandState: sourceCount === SOURCE_KEYS.length ? 'all-sources' : sourceCount > 0 ? 'partial' : 'failed',
      exhaustiveVerified,
      inventoryState: inventory?.tier || (mapped ? 'mapped' : 'missing'),
      capturedOffers: Number(inventory?.n_offers || 0),
      capturedMarkets: countMarkets(inventory),
      joined,
      joinedOffers: Number(marriage?.married || 0),
      orphanOffers: Number(marriage?.orphans || 0),
      matchRate: Number(marriage?.match_rate_pct || 0),
      currentReleaseState: joined ? releaseState : 'not-in-release',
      continuousState: normalizeContinuousState(book, joined ? releaseState : 'not-in-release'),
      completeness: completenessBooks[key] || null,
      lastCheckedAt: latestSourceCheck(book?.sources),
      proofUrl: `https://abet-scraper-proof-portal.vercel.app${proofPath}`,
    }
  }).sort((a, b) => {
    const severity = (row) => {
      if (!row.anyLive) return 0
      if (row.sourceCount < SOURCE_KEYS.length) return 1
      if (!row.exhaustiveVerified) return 2
      if (!row.joined) return 3
      if (row.continuousState !== 'running') return 4
      return 5
    }
    return severity(a) - severity(b) || a.display.localeCompare(b.display)
  })
}

export function summarizeScraperCoverage(bundle, rows, nowMs = Date.now()) {
  const activeRows = rows.filter((row) => row.active)
  const masterBooks = asArray(bundle?.masterList)
  const masterActiveBooks = masterBooks.filter((book) => book?.status !== 'inactive' && book?.defunct !== true)
  const masterBuiltBooks = masterBooks.filter((book) => book?.scraper_status === 'built')
  const capturedAt = bundle?.manifest?.captured_utc || bundle?.release?.captured_utc || null
  const capturedMs = parseTime(capturedAt)
  return {
    registeredBooks: rows.length,
    masterBooks: masterBooks.length,
    masterActiveBooks: masterActiveBooks.length,
    masterBuiltBooks: masterBuiltBooks.length,
    masterGapBooks: masterBooks.length - masterBuiltBooks.length,
    activeBooks: activeRows.length,
    anyLiveBooks: activeRows.filter((row) => row.anyLive).length,
    allSourceBooks: activeRows.filter((row) => row.sourceCount === SOURCE_KEYS.length).length,
    expectedSourceCells: Number(bundle?.fleet?.summary?.expected_primary_cells || activeRows.length * SOURCE_KEYS.length),
    okSourceCells: Number(bundle?.fleet?.summary?.ok_primary_cells || activeRows.reduce((sum, row) => sum + row.sourceCount, 0)),
    inventoryBooks: Number(bundle?.inventory?.n_books || rows.filter((row) => row.inventoryState !== 'missing').length),
    mappedBooks: Number(bundle?.inventory?.n_mapped || rows.filter((row) => row.inventoryState === 'mapped-exact').length),
    exhaustiveBooks: activeRows.filter((row) => row.exhaustiveVerified).length,
    joinedBooks: activeRows.filter((row) => row.joined).length,
    continuousBooks: activeRows.filter((row) => row.continuousState === 'running').length,
    totalOffers: Number(bundle?.marriage?.summary?.total_offers || 0),
    orphanOffers: Number(bundle?.marriage?.summary?.orphans_total || 0),
    releaseCapturedAt: capturedAt,
    releaseAgeMs: capturedMs == null ? null : Math.max(0, nowMs - capturedMs),
  }
}

export async function fetchScraperCoverageBundle() {
  const requests = {
    fleet: fetchArtifact(BOARD_DATA_BASE_URL, 'fleet_truth.json', 25000),
    manifest: fetchArtifact(BOARD_DATA_BASE_URL, 'crossbook_allbets_manifest.json'),
    completeness: fetchArtifact(BOARD_DATA_BASE_URL, 'completeness.json'),
    release: fetchArtifact(BOARD_DATA_BASE_URL, 'release_manifest.json'),
    matrix: fetchArtifact(PROOF_DATA_BASE_URL, 'scraper_matrix.json'),
    inventory: fetchArtifact(PROOF_DATA_BASE_URL, 'crossbook_offer_inventory.json', 25000),
    marriage: fetchArtifact(PROOF_DATA_BASE_URL, 'marriage_integrity.json'),
    masterList: fetchJsonUrl(MASTER_LIST_URL, 25000),
  }
  const entries = await Promise.all(Object.entries(requests).map(async ([key, request]) => {
    try {
      return [key, await request, null]
    } catch (error) {
      return [key, null, error.message]
    }
  }))
  const bundle = {}
  const errors = []
  entries.forEach(([key, value, error]) => {
    bundle[key] = value
    if (error) errors.push(`${key}: ${error}`)
  })
  if (!bundle.fleet) throw new Error(errors.join(' | ') || 'Fleet truth unavailable')
  return { bundle, errors }
}

export { SOURCE_KEYS }
